import type { AdminUser, UserRole, UserStatus } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { AdminPage } from '@/features/admin/admin-layout';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FormField } from '@/features/shared/form-field';
import { formatBytes, formatDate } from '@/features/shared/labels';
import { useDebounced } from '@/features/shared/recipe-picker';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

type PendingChange = { user: AdminUser; status?: UserStatus; role?: UserRole };
const ALL = '__all__';

export function AdminUsersPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>(ALL);
  const [role, setRole] = useState<string>(ALL);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [reason, setReason] = useState('');
  const [supportUser, setSupportUser] = useState<AdminUser | null>(null);
  const query = useDebounced(q.trim());
  const params = {
    q: query || undefined,
    status: status === ALL ? undefined : status,
    role: role === ALL ? undefined : role,
  };

  const users = useInfiniteQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: ({ pageParam }) => api.admin.users({ ...params, cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = users.data?.pages.flatMap((p) => p.items) ?? [];

  const update = useMutation({
    mutationFn: (change: PendingChange) =>
      api.admin.updateUser(change.user.id, {
        status: change.status,
        role: change.role,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setPending(null);
      setReason('');
      toast.success(t('admin.users.updated'));
    },
    onError: (e) => toastError(e),
  });

  return (
    <AdminPage section="users">
      <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <div>
          <Label htmlFor="user-search" className="sr-only">
            {t('common.search')}
          </Label>
          <Input
            id="user-search"
            type="search"
            placeholder={t('admin.users.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-user-search"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger aria-label={t('admin.users.status')} className="sm:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin.users.allStatuses')}</SelectItem>
            {(['ACTIVE', 'DEACTIVATED', 'PENDING_DELETION'] as UserStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {t(`enums.userStatus.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger aria-label={t('admin.users.role')} className="sm:w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('admin.users.allRoles')}</SelectItem>
            <SelectItem value="USER">{t('enums.userRole.USER')}</SelectItem>
            <SelectItem value="ADMIN">{t('enums.userRole.ADMIN')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {users.isLoading && <Spinner label={t('app.loading')} />}
      {users.error && <Alert variant="destructive">{describeError(users.error)}</Alert>}
      {users.data && (
        <ResponsiveTable
          caption={t('admin.sections.users')}
          rowKey={(u) => u.id}
          rows={rows}
          empty={t('common.noResults')}
          columns={[
            {
              key: 'user',
              header: t('admin.users.user'),
              cell: (u) => (
                <span>
                  <Link
                    to={`/admin/users/${u.id}`}
                    className="block font-medium text-primary underline-offset-4 hover:underline"
                    aria-label={t('admin.users.detailsFor', { name: u.displayName })}
                    data-testid="admin-user-link"
                  >
                    {u.displayName}
                  </Link>
                  <span className="block text-xs text-muted-foreground">{u.email}</span>
                </span>
              ),
            },
            {
              key: 'ai',
              header: t('admin.users.ai'),
              cell: (u) => (
                <Badge
                  variant={u.aiEnabled ? 'success' : 'outline'}
                  data-testid="admin-user-ai-badge"
                >
                  {t(u.aiEnabled ? 'admin.users.aiOn' : 'admin.users.aiOff')}
                </Badge>
              ),
            },
            {
              key: 'role',
              header: t('admin.users.role'),
              cell: (u) => (
                <Badge variant={u.role === 'ADMIN' ? 'default' : 'outline'}>
                  {t(`enums.userRole.${u.role}`)}
                </Badge>
              ),
            },
            {
              key: 'status',
              header: t('admin.users.status'),
              cell: (u) => (
                <Badge
                  variant={
                    u.status === 'ACTIVE'
                      ? 'success'
                      : u.status === 'DEACTIVATED'
                        ? 'destructive'
                        : 'warning'
                  }
                >
                  {t(`enums.userStatus.${u.status}`)}
                </Badge>
              ),
            },
            { key: 'recipes', header: t('admin.users.recipes'), cell: (u) => u.recipeCount },
            {
              key: 'storage',
              header: t('admin.users.storage'),
              cell: (u) => formatBytes(u.storageBytes),
            },
            {
              key: 'lastLogin',
              header: t('admin.users.lastLogin'),
              cell: (u) => formatDate(u.lastLoginAt, i18n.language, true) || '–',
            },
            {
              key: 'actions',
              header: '',
              label: t('admin.actions'),
              cell: (u) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('admin.users.actionsFor', { name: u.displayName })}
                      data-testid="admin-user-actions"
                    >
                      <MoreHorizontal aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => void navigate(`/admin/users/${u.id}`)}>
                      {t('admin.users.details')}
                    </DropdownMenuItem>
                    {u.status === 'ACTIVE' ? (
                      <DropdownMenuItem
                        destructive
                        onSelect={() => setPending({ user: u, status: 'DEACTIVATED' })}
                      >
                        {t('admin.users.deactivate')}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onSelect={() => setPending({ user: u, status: 'ACTIVE' })}>
                        {t('admin.users.activate')}
                      </DropdownMenuItem>
                    )}
                    {u.role === 'USER' ? (
                      <DropdownMenuItem onSelect={() => setPending({ user: u, role: 'ADMIN' })}>
                        {t('admin.users.makeAdmin')}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onSelect={() => setPending({ user: u, role: 'USER' })}>
                        {t('admin.users.removeAdmin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={() => setSupportUser(u)}>
                      {t('admin.users.supportAccess')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      )}
      {users.hasNextPage && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            loading={users.isFetchingNextPage}
            onClick={() => void users.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        onOpenChange={(o) => !o && setPending(null)}
        title={t('admin.users.confirmTitle')}
        description={
          pending
            ? t('admin.users.confirmBody', {
                name: pending.user.displayName,
                change: pending.status
                  ? t(`enums.userStatus.${pending.status}`)
                  : t(`enums.userRole.${pending.role ?? 'USER'}`),
              })
            : undefined
        }
        destructive={pending?.status === 'DEACTIVATED'}
        loading={update.isPending}
        onConfirm={() => {
          if (pending) update.mutate(pending);
        }}
      >
        <FormField label={t('admin.users.reason')} optionalText={t('common.optional')}>
          <Textarea
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="admin-user-reason"
          />
        </FormField>
      </ConfirmDialog>
      <SupportAccessDialog user={supportUser} onClose={() => setSupportUser(null)} />
    </AdminPage>
  );
}

function SupportAccessDialog({ user, onClose }: { user: AdminUser | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [ttl, setTtl] = useState('60');
  const grant = useMutation({
    mutationFn: () =>
      api.admin.grantSupportAccess({
        userId: user?.id ?? '',
        reason: reason.trim(),
        ttlMinutes: Number(ttl),
      }),
    onSuccess: (g) => {
      toast.success(
        t('admin.users.supportGranted', { until: formatDate(g.expiresAt, i18n.language, true) }),
      );
      setReason('');
      onClose();
    },
    onError: (e) => toastError(e),
  });
  return (
    <Dialog open={Boolean(user)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('admin.users.supportAccess')}</DialogTitle>
          <DialogDescription>
            {t('admin.users.supportDescription', { name: user?.displayName ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length >= 10) grant.mutate();
          }}
        >
          <FormField label={t('admin.users.reason')} required hint={t('admin.users.reasonHint')}>
            <Textarea
              rows={3}
              minLength={10}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormField>
          <FormField label={t('admin.users.ttl')} required>
            <Input
              type="number"
              min={5}
              max={240}
              inputMode="numeric"
              value={ttl}
              onChange={(e) => setTtl(e.target.value)}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={grant.isPending} disabled={reason.trim().length < 10}>
              {t('admin.users.grant')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
